import React, { Suspense } from 'react';
import { HashRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import '@sequelae/lore-core/dist/styles/index.css';
import { Spinner } from '@sequelae/lore-core';
import { ThemeProvider } from './contexts/ThemeContext';
import {
  ComponentPlaygroundSkeleton,
  NavigationTestSkeleton,
  AccessibilityDemoSkeleton
} from './components/PageSkeletons';
import {
  useBridgeRouteObserver,
  registerForNavigationDeeplink,
  registerForNavigationGoBack,
  registerForLifecycleEvents
} from '@sequelae/lore-mfe-kit';

const Settings = React.lazy(() => import('./components/Settings'));
const ProfileDetails = React.lazy(() => import('./components/ProfileDetails'));
const TechnicalSupport = React.lazy(() => import('./components/TechnicalSupport'));
const Activity = React.lazy(() => import('./components/Activity'));
const MfeKitTestHub = React.lazy(() => import('./components/MfeKitTestHub'));
const MfeKitNavigation = React.lazy(() => import('./components/MfeKitNavigation'));
const MfeKitDeepLinks = React.lazy(() => import('./components/MfeKitDeepLinks'));
const MfeKitAPIs = React.lazy(() => import('./components/MfeKitAPIs'));
const MfeKitEdgeCases = React.lazy(() => import('./components/MfeKitEdgeCases'));
const ComponentPlayground = React.lazy(() => import('./components/ComponentPlayground'));
const ComponentDemoPage = React.lazy(() => import('./components/demos/ComponentDemoPage'));
const AccessibilityDemo = React.lazy(() => import('./components/AccessibilityDemo'));

type CommonMfeProps = {
  hostAuthResult: any; // Replace 'any' with the actual type if you know it
  externalRefUserId: string;
  onFinished?: () => void; // Optional callback to close/dismiss the MFE
};

function App(props: CommonMfeProps) {
  //useClickEventTracker();

  React.useEffect(() => {
    console.log('Received MFE props:', props);
    // Example usage:
    //const token = props.onGetAccessToken();
    //console.log('Access token:', token);

    console.log('Host Auth Result:', props.hostAuthResult);
    console.log('External Ref User ID:', props.externalRefUserId);
  }, [props]);

  console.log('App component rendering');
  //const token = props.onGetAccessToken();
  console.log('Environment variables:', {
    REACT_APP_ENV: process.env.REACT_APP_ENV,
    REACT_APP_API_BASE_URL: process.env.REACT_APP_API_BASE_URL,
    //REACT_APP_AUTH_TOKEN: process.env.REACT_APP_AUTH_TOKEN ? 'Token exists' : 'No token'
    //REACT_APP_AUTH_TOKEN: token,
  });

  // Get ref_user_id from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  console.log('URL Parameters:', Object.fromEntries(urlParams.entries()));

  const urlUserId = urlParams.get('ref_user_id');
  console.log('User ID from URL:', urlUserId);

  const localStorageUserId = window.localStorage.getItem('ref_user_id');
  console.log('User ID from localStorage:', localStorageUserId);

  const refUserId = React.useMemo(() => {
    const resolved = urlUserId ?? localStorageUserId ?? '44016'; // Fallback to default if not provided
    console.log('Final resolved User ID in App:', resolved);
    return resolved;
  }, [urlUserId, localStorageUserId]);

  // Store in localStorage for future use (only when it changes)
  React.useEffect(() => {
    if (refUserId && refUserId !== localStorageUserId) {
      window.localStorage.setItem('ref_user_id', refUserId);
      console.log('Stored refUserId in localStorage:', refUserId);
    }
  }, [refUserId, localStorageUserId]);

  return (
    <ThemeProvider>
      <HashRouter>
        <AppContent
          userId={refUserId}
          onFinished={props.onFinished}
        />
      </HashRouter>
    </ThemeProvider>
  );
}

// Define valid internal routes for Diagnostics MFE (constant, outside component)
const VALID_INTERNAL_ROUTES = [
  '/', // Redirects to /settings (original default)
  '/settings', // Original settings page (default)
  '/settings/profile', // Profile details (Personal Info, Account Status, System Info)
  '/settings/support', // Technical support
  '/activity', // Keep for backward compatibility
  '/diagnostics/activity', // New MFE-prefixed route (recommended for deep links)
  '/mfe-kit-tests',
  '/mfe-kit-navigation',
  '/mfe-kit-deep-links',
  '/mfe-kit-apis',
  '/mfe-kit-edge-cases',
  '/components', // Lore Core Examples
  '/accessibility', // Accessibility demo
] as const;

// Check if route matches /components/:name pattern
const isComponentDemoRoute = (path: string): boolean => {
  return /^\/components\/[a-z-]+$/.test(path);
};

// Check if a route is external (should be handled by bridge)
const isExternalRoute = (path: string): boolean => {
  const externalRoutePatterns = [
    /^\/rewards\//,
    /^\/community\//,
    /^\/explorations\//,
    /^\/lorebot\//,
    /^\/account\//,
    /^\/streaks/,
    /^\/verification/
  ];
  return externalRoutePatterns.some(pattern => pattern.test(path));
};

// Separate component to use Router hooks
function AppContent({
  userId,
  onFinished
}: {
  userId: string;
  onFinished?: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize bridge route observer for external navigation (inside Router context)
  useBridgeRouteObserver([
    /^\/rewards\//,
    /^\/community\//,
    /^\/explorations\//,
    /^\/lorebot\//,
    /^\/account\//
  ]);

  // Restore last route on mount
  React.useEffect(() => {
    const savedRoute = window.localStorage.getItem('diagnostics_last_route');
    if (savedRoute && savedRoute !== location.pathname) {
      console.log('🔄 Restoring last route from localStorage:', savedRoute);
      navigate(savedRoute, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Save current route whenever it changes
  React.useEffect(() => {
    console.log('💾 Saving current route to localStorage:', location.pathname);
    window.localStorage.setItem('diagnostics_last_route', location.pathname);
  }, [location.pathname]);

  // Handle lifecycle events using mfe-kit hook
  registerForLifecycleEvents({
    onViewWillDisappear: () => {
      console.log('👋 View disappearing - current route saved:', location.pathname);
      window.localStorage.setItem('diagnostics_last_route', location.pathname);
    },
    onViewDidAppear: () => {
      console.log('👁️ View appeared - restoring route if needed');
      const savedRoute = window.localStorage.getItem('diagnostics_last_route');
      if (savedRoute && savedRoute !== location.pathname) {
        console.log('🔄 Restoring route on appear:', savedRoute);
        navigate(savedRoute, { replace: true });
      }
    },
  });

  // Handle deep link navigation using mfe-kit hook
  registerForNavigationDeeplink({
    onDeeplink: (path, data) => {
      console.log('🔗 Deep link navigation requested via hook:', path, data);

      // Normalize path (ensure it starts with /)
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;

      // Check if it's an external route
      if (isExternalRoute(normalizedPath)) {
        console.log('ℹ️ Deep link path is external, should be handled by bridge:', normalizedPath);
        return;
      }

      // Check if it's a valid internal route (static routes or /components/:name pattern)
      const isValidStaticRoute = (VALID_INTERNAL_ROUTES as readonly string[]).includes(normalizedPath);
      const isValidDemoRoute = isComponentDemoRoute(normalizedPath);
      if (!isValidStaticRoute && !isValidDemoRoute) {
        console.warn('⚠️ Invalid internal route for deep link:', normalizedPath);
        console.log('📋 Valid internal routes:', VALID_INTERNAL_ROUTES);
        return;
      }

      // Check if we're already on this route
      if (location.pathname === normalizedPath) {
        console.log('ℹ️ Already on target route:', normalizedPath);
        return;
      }

      // Navigate to the route
      console.log('🔗 Navigating to deep link route:', normalizedPath);
      navigate(normalizedPath);
    }
  });

  // Handle go back navigation using mfe-kit hook
  registerForNavigationGoBack({
    onGoBack: () => {
      console.log('⬅️ Go back navigation requested via hook');

      // Get current pathname
      const currentPath = location.pathname;

      // Check if we're at root routes where back should close MFE
      const isAtRoot = currentPath === '/settings' || currentPath === '/';

      if (isAtRoot) {
        console.log('⚠️ Already at root route, will signal native to close MFE');
        return false; // Tell native to pop/close the screen
      }

      // Navigate back in history
      console.log('⬅️ Navigating back from:', currentPath);
      navigate(-1);
      return true; // We handled it internally
    },
    autoFinishWhenAtRoot: true // Automatically calls CommunicationBridge.onFinished() when at root
  });

  // Check if initialPath is present in URL (from iOS MFEType.custom)
  const initialPath = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('initialPath');
  }, []);

  return (
    <div className="App">
      <div className="content">
        <Suspense fallback={
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
            <Spinner size="md" />
          </div>
        }>
          <Routes>
            <Route
              path="/"
              element={initialPath ? <Navigate to={initialPath} replace /> : <Navigate to="/settings" replace />}
            />
            <Route path="/settings" element={<Settings userId={userId} />} />
            <Route path="/settings/profile" element={<ProfileDetails userId={userId} />} />
            <Route path="/settings/support" element={<TechnicalSupport userId={userId} />} />
            <Route path="/activity" element={<Activity userId={userId} />} />
            <Route path="/diagnostics/activity" element={<Activity userId={userId} />} />
            <Route path="/mfe-kit-tests" element={
              <Suspense fallback={<NavigationTestSkeleton />}>
                <MfeKitTestHub />
              </Suspense>
            } />
            <Route path="/mfe-kit-navigation" element={<MfeKitNavigation userId={userId} />} />
            <Route path="/mfe-kit-deep-links" element={<MfeKitDeepLinks />} />
            <Route path="/mfe-kit-apis" element={<MfeKitAPIs />} />
            <Route path="/mfe-kit-edge-cases" element={<MfeKitEdgeCases />} />
            <Route path="/components" element={
              <Suspense fallback={<ComponentPlaygroundSkeleton />}>
                <ComponentPlayground />
              </Suspense>
            } />
            <Route path="/components/:name" element={<ComponentDemoPage />} />
            <Route path="/accessibility" element={
              <Suspense fallback={<AccessibilityDemoSkeleton />}>
                <AccessibilityDemo userId={userId} />
              </Suspense>
            } />
          </Routes>
        </Suspense>
      </div>
    </div>
  );
}

export default App;
